// src/components/Header.jsx
import React, { useEffect, useId, useState, useCallback } from 'react';
import { Link, NavLink } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { FiMenu, FiX } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { siteConfig, getWhatsAppUrl } from '@/lib/siteConfig';

export default function Header() {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  // Bloqueia/desbloqueia scroll ao abrir/fechar menu mobile
  const applyBodyLock = useCallback((nextOpen) => {
    document.body.classList.toggle('nav-open', nextOpen);
  }, []);

  const toggleMenu = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      applyBodyLock(next);
      return next;
    });
  }, [applyBodyLock]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    applyBodyLock(false);
  }, [applyBodyLock]);

  // Fecha com ESC e em mudanças de hash
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
    const onHash = () => closeMenu();
    window.addEventListener('keydown', onKey);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('hashchange', onHash);
      document.body.classList.remove('nav-open');
    };
  }, [closeMenu]);

  return (
    <header className="header" role="banner">
      {/* Skip link (A11y) — estilize .skip-link no CSS para aparecer ao focar */}
      <a href="#conteudo" className="skip-link">Pular para o conteúdo</a>

      <div className="container">
        {/* LOGO */}
        <Link
          to="/"
          className="logo"
          aria-label={`${siteConfig?.nome || 'CLEH TECH'} — Início`}
          onClick={closeMenu}
        >
          {siteConfig?.nome || 'CLEH TECH'}
        </Link>

        {/* WRAPPER */}
        <div className={`nav ${open ? 'is-open' : ''}`}>
          {/* TOGGLE MOBILE */}
          <button
            className="nav-toggle"
            aria-label={open ? 'Fechar menu de navegação' : 'Abrir menu de navegação'}
            aria-controls={panelId}
            aria-expanded={open}
            aria-haspopup="true"
            onClick={toggleMenu}
            type="button"
          >
            {open ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>

          {/* LISTA DESKTOP */}
          <ul className="nav-list" aria-label="Navegação principal (desktop)">
            <li>
              <NavLink
                to="/"
                className={({ isActive }) => `nav-link ${isActive ? 'is-active' : ''}`}
                onClick={closeMenu}
              >
                Início
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/servicos"
                className={({ isActive }) => `nav-link ${isActive ? 'is-active' : ''}`}
                onClick={closeMenu}
              >
                Serviços
              </NavLink>
            </li>
            <li>
              {/* ancora interna para a seção de contato da Home */}
              <a className="nav-link" href="#contato" onClick={closeMenu}>
                Contato
              </a>
            </li>
          </ul>

          {/* Tema (desktop) */}
          <div className="theme-toggle">
            <ThemeToggle />
          </div>

          {/* DRAWER MOBILE */}
          <div
            id={panelId}
            className="nav-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            onClick={(e) => {
              const el = e.target;
              if (el.closest('a')) closeMenu();
            }}
          >
            <ul className="nav-list" aria-label="Navegação principal (mobile)">
              <li>
                <Link className="nav-link" to="/" onClick={closeMenu}>
                  Início
                </Link>
              </li>
              <li>
                <Link className="nav-link" to="/servicos" onClick={closeMenu}>
                  Serviços
                </Link>
              </li>
              <li>
                <a className="nav-link" href="#contato" onClick={closeMenu}>
                  Contato
                </a>
              </li>
            </ul>

            <div className="nav-cta">
              <a
                className="btn btn--primary"
                href={getWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Falar no WhatsApp"
              >
                <FaWhatsapp size={18} />
                WhatsApp
              </a>
            </div>

            <div className="theme-toggle">
              <ThemeToggle />
            </div>
          </div>
        </div>

        {/* OVERLAY (mobile) */}
        <div className="nav-overlay" onClick={closeMenu} aria-hidden="true" />
      </div>
    </header>
  );
}
